import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';

export interface PageHeaderProps {
    title: string | React.ReactNode;
    subtitle?: string | React.ReactNode;
    actions?: React.ReactNode;
    badge?: React.ReactNode;
    breadcrumb?: Array<{
        label: string;
        path?: string;
        onClick?: () => void;
    }>;
    className?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    subtitle,
    actions,
    badge,
    breadcrumb,
    className = '',
}) => {
    const navigate = useNavigate();

    return (
        <div
            className={cn(
                'mb-6 pb-5 border-b border-border',
                className
            )}
        >
            {breadcrumb && breadcrumb.length > 0 && (
                <nav className="mb-2" aria-label="Breadcrumb">
                    <ol className="flex items-center gap-1 text-xs text-muted-foreground">
                        {breadcrumb.map((item, index) => (
                            <li key={index} className="flex items-center gap-1">
                                {item.path ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (item.onClick) {
                                                item.onClick();
                                            } else {
                                                navigate(item.path!);
                                            }
                                        }}
                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        {item.label}
                                    </button>
                                ) : (
                                    <span className="text-foreground">{item.label}</span>
                                )}
                                {index < breadcrumb.length - 1 && (
                                    <ChevronRight className="h-3 w-3 text-neutral-400" />
                                )}
                            </li>
                        ))}
                    </ol>
                </nav>
            )}
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-6">
                <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        {typeof title === 'string' ? (
                            <h1 className="text-xl font-semibold leading-tight tracking-tight text-foreground">
                                {title}
                            </h1>
                        ) : (
                            <div className="text-xl font-semibold leading-tight tracking-tight text-foreground">
                                {title}
                            </div>
                        )}
                        {badge && <div className="flex items-center">{badge}</div>}
                    </div>
                    {subtitle &&
                        (typeof subtitle === 'string' ? (
                            <p className="text-sm text-muted-foreground">{subtitle}</p>
                        ) : (
                            <div className="text-sm text-muted-foreground">{subtitle}</div>
                        ))}
                </div>
                {actions && (
                    <div className="flex items-center gap-2 flex-wrap shrink-0">{actions}</div>
                )}
            </div>
        </div>
    );
};

export default PageHeader;
